import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import MetaTags from '@/components/SEO/MetaTags';
import BrandLogo from '@/components/brand/BrandLogo';
import { LAUNCH_OFFER } from '@/utils/payments/launchOffer';
import { recordLaunchEvent } from '@/utils/payments/launchMetrics';

const SAMPLE = {
  report: 'Sample report line: “Invasive carcinoma, Nottingham grade 2.” This is not a real patient record.',
  explanation:
    'Sample explanation: those words name a kind of breast cancer and a middle grade on a three-step scale. A companion would suggest asking the oncology team what the grade means for this person and which questions belong at the next visit. This sample was written for the page. No clinician reviewed it, and no file was uploaded.',
};

export default function LaunchOfferPage() {
  useEffect(() => {
    recordLaunchEvent('landing_view');
  }, []);

  const showSample = () => {
    recordLaunchEvent('sample_viewed');
    const node = document.getElementById('launch-sample');
    if (node) node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-cream-paper text-forest-ink">
      <MetaTags
        title="Understand a report before the visit"
        description="ByOnco explains report wording in plain language, keeps the conversation, and helps you note symptoms for an appointment. $19.99 for 30 days, charged once. AI, not a clinician."
        path="/start"
      />
      <header className="max-w-3xl mx-auto px-4 pt-6 sm:pt-10 flex items-center justify-between gap-4">
        <BrandLogo size="md" />
        <Link to="/subscribe?plan=monthly&redirect=%2Fapp" className="text-sm underline underline-offset-4">
          {LAUNCH_OFFER.priceLabel}
        </Link>
      </header>

      <main className="max-w-3xl mx-auto px-4 pb-16 pt-8 sm:pt-12">
        <p className="text-sm uppercase tracking-wide text-charcoal/70">For adults and families</p>
        <h1 className="mt-3 text-3xl sm:text-5xl font-light leading-tight">
          Questions ready before the next visit
        </h1>
        <p className="mt-4 text-base sm:text-lg text-charcoal/80 leading-relaxed max-w-xl">
          Upload a report, ask what the words mean, and leave with questions for the care team.
          ByOnco is an AI companion. It does not diagnose or replace a clinician.
        </p>

        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <Link
            to="/subscribe?plan=monthly&redirect=%2Fapp"
            className="inline-flex justify-center items-center rounded-full bg-forest-ink text-white px-6 py-3 text-sm font-medium"
          >
            Continue for {LAUNCH_OFFER.priceLabel}
          </Link>
          <button
            type="button"
            onClick={showSample}
            className="inline-flex justify-center items-center rounded-full border border-forest-ink px-6 py-3 text-sm"
          >
            View the sample
          </button>
        </div>

        <ul className="mt-10 space-y-3 text-sm sm:text-base">
          {LAUNCH_OFFER.included.map((line) => (
            <li key={line} className="border border-border-mist bg-white rounded-2xl px-4 py-3">
              {line}
            </li>
          ))}
        </ul>

        <section id="launch-sample" className="mt-10 rounded-2xl border border-border-mist bg-white p-5 sm:p-6">
          <p className="text-xs uppercase tracking-wide text-charcoal/70">Sample data, not your record</p>
          <h2 className="mt-2 text-xl font-normal">What a report explanation looks like</h2>
          <p className="mt-4 text-sm leading-relaxed">{SAMPLE.report}</p>
          <p className="mt-4 text-sm leading-relaxed text-charcoal/90">{SAMPLE.explanation}</p>
        </section>

        <section className="mt-10" aria-labelledby="launch-offer-heading">
          <h2 id="launch-offer-heading" className="text-2xl font-light">The offer</h2>
          <p className="mt-3 text-3xl font-medium">{LAUNCH_OFFER.priceLabel} <span className="text-base font-normal text-charcoal/70">{LAUNCH_OFFER.termLabel}</span></p>
          <p className="mt-2 text-sm">{LAUNCH_OFFER.billingLabel}</p>
          <p className="mt-2 text-sm">{LAUNCH_OFFER.usageLabel}</p>
          <ul className="mt-4 space-y-2 text-sm text-charcoal/80">
            {LAUNCH_OFFER.limits.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          <Link
            to="/subscribe?plan=monthly&redirect=%2Fapp"
            className="mt-6 inline-flex justify-center items-center rounded-full bg-forest-ink text-white px-6 py-3 text-sm font-medium"
          >
            Continue for {LAUNCH_OFFER.priceLabel}
          </Link>
          <p className="mt-4 text-sm">
            Questions or a refund request: <a className="underline" href={`mailto:${LAUNCH_OFFER.supportEmail}`}>{LAUNCH_OFFER.supportEmail}</a>
          </p>
          <p className="mt-3 text-sm flex flex-wrap gap-4">
            <Link className="underline" to="/privacy">Privacy</Link>
            <Link className="underline" to="/terms-and-conditions">Terms</Link>
            <Link className="underline" to="/medical-disclaimer">Medical disclaimer</Link>
          </p>
        </section>
      </main>
    </div>
  );
}
